import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import StreamIcon from '@mui/icons-material/Stream';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useNavigate } from 'react-router-dom';

import { ActiveStream, getAdminActiveStreams, killStream, suspendChannel, unsuspendChannel } from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { formatCodecPair } from '../../utils/codecLabels';

type ProtocolFilter = 'all' | 'live' | 'offline' | 'rtmpConn' | 'srtConn' | 'low_bitrate' | 'viewers' | 'issues';
type NocViewMode = 'list' | 'grid';

const NOC_VIEW_STORAGE_KEY = 'streamy.admin.streamNoc.viewMode';

const cardSx = { borderRadius: 0, border: '0.5px solid', borderColor: 'divider', bgcolor: 'background.paper' } as const;
const labelSx = { fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'text.disabled' };
const monoSx = { fontFamily: "'DM Mono', monospace", fontSize: '0.76rem', fontWeight: 800 };
const iconSx = { borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 30, height: 30 } as const;

const formatBitrate = (bps?: number) => {
  const mbps = Number(bps || 0) / 1000000;
  if (mbps <= 0) return '0.00 Mbps';
  return `${mbps >= 10 ? mbps.toFixed(1) : mbps.toFixed(2)} Mbps`;
};

const formatBytes = (bytes?: number) => {
  const value = Number(bytes || 0);
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(2)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(1)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${value} B`;
};

const formatUptime = (sec?: number) => {
  const total = Number(sec || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const bitrateSeverity = (bps?: number) => {
  const mbps = Number(bps || 0) / 1000000;
  if (mbps > 0 && mbps < 1) return 'error';
  if (mbps > 0 && mbps < 3) return 'warning';
  return 'success';
};

const sourceTypeKey = (sourceType?: string) => {
  const value = String(sourceType || '').toLowerCase();
  if (value === 'rtmpconn' || value === 'rtmp') return 'rtmp';
  if (value === 'srtconn' || value === 'srt') return 'srt';
  return value || 'input';
};
const protocolLabel = (sourceType?: string) => sourceTypeKey(sourceType) === 'rtmp' ? 'RTMP' : sourceTypeKey(sourceType) === 'srt' ? 'SRT' : (sourceType || 'INPUT');
const sourceModeLabel = (sourceMode?: string | null) => sourceMode === 'MEDIABOX' ? 'MediaBox' : sourceMode === 'PLAYKASTR' ? 'PlayKaster' : 'RTMP';
const isStreamLive = (stream: ActiveStream) => Boolean(stream.sourceReady && stream.status === 'live');

const SummaryMetric = ({ label, value, helper, color }: { label: string; value: React.ReactNode; helper?: string; color: string }) => (
  <Box sx={{ minWidth: 126, borderLeft: `3px solid ${color}`, pl: 1.25 }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.16rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
    {helper && <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{helper}</Typography>}
  </Box>
);

export const StreamMonitorPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [streams, setStreams] = useState<ActiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ProtocolFilter>('all');
  const [viewMode, setViewMode] = useState<NocViewMode>(() => {
    try {
      return window.localStorage.getItem(NOC_VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  const [killTarget, setKillTarget] = useState<ActiveStream | null>(null);
  const [killConfirm, setKillConfirm] = useState('');
  const [killReason, setKillReason] = useState('');
  const [killLoading, setKillLoading] = useState(false);

  const fetchStreams = async () => {
    try {
      const data = await getAdminActiveStreams();
      setStreams(data || []);
    } catch (error) {
      console.error(error);
      showSnackbar('Failed to load active streams', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStreams();
    const interval = setInterval(fetchStreams, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(NOC_VIEW_STORAGE_KEY, viewMode);
    } catch {
      // A restricted browser storage policy should not block NOC rendering.
    }
  }, [viewMode]);

  const stats = useMemo(() => ({
    total: streams.length,
    live: streams.filter(isStreamLive).length,
    offline: streams.filter((stream) => !isStreamLive(stream)).length,
    totalReaders: streams.reduce((acc, s) => acc + Number(s.viewers || 0), 0),
    totalBandwidth: streams.reduce((acc, s) => acc + Number(s.bitrate || 0), 0),
    totalBytesIn: streams.reduce((acc, s) => acc + Number(s.bytesIn || 0), 0),
    activeOutputs: streams.reduce((acc, s) => acc + Number(s.outputs?.active || 0), 0),
    rtmpCount: streams.filter((s) => isStreamLive(s) && sourceTypeKey(s.sourceType) === 'rtmp').length,
    srtCount: streams.filter((s) => isStreamLive(s) && sourceTypeKey(s.sourceType) === 'srt').length,
    lowBitrate: streams.filter((s) => Number(s.bitrate || 0) > 0 && Number(s.bitrate || 0) < 1000000).length,
    channelIssues: streams.filter((s) => !isStreamLive(s) || s.channelActive === false || s.billingSuspended || s.scheduledDeletion).length,
    scheduledDeletion: streams.filter((s) => !!s.scheduledDeletion).length,
    scheduledOutputRelief: streams.filter((s) => !!s.scheduledDeletion).reduce((acc, s) => acc + Number(s.outputs?.active || 0), 0),
  }), [streams]);
  const loadShedPercent = stats.activeOutputs > 0 ? Math.round((stats.scheduledOutputRelief / stats.activeOutputs) * 100) : 0;

  const filtered = useMemo(() => streams
    .filter((stream) => {
      const term = search.toLowerCase();
      const haystack = [
        stream.name,
        stream.pathName,
        stream.channelName,
        stream.sourceId,
        stream.userEmail,
        stream.userName,
        ...(stream.outputLabels || []),
      ].filter(Boolean).join(' ').toLowerCase();
      const matches = !term || haystack.includes(term);
      if (!matches) return false;
      if (filter === 'live') return isStreamLive(stream);
      if (filter === 'offline') return !isStreamLive(stream);
      if (filter === 'rtmpConn') return isStreamLive(stream) && sourceTypeKey(stream.sourceType) === 'rtmp';
      if (filter === 'srtConn') return isStreamLive(stream) && sourceTypeKey(stream.sourceType) === 'srt';
      if (filter === 'viewers') return Number(stream.viewers || 0) > 0;
      if (filter === 'low_bitrate') return Number(stream.bitrate || 0) > 0 && Number(stream.bitrate || 0) < 1000000;
      if (filter === 'issues') return !isStreamLive(stream) || Number(stream.bitrate || 0) > 0 && Number(stream.bitrate || 0) < 1000000 || stream.channelActive === false || Boolean(stream.billingSuspended) || Boolean(stream.scheduledDeletion);
      return true;
    })
    .sort((a, b) => Number(isStreamLive(b)) - Number(isStreamLive(a)) || String(a.channelName || a.name || '').localeCompare(String(b.channelName || b.name || ''))), [filter, search, streams]);

  const confirmKill = (stream: ActiveStream) => {
    setKillTarget(stream);
    setKillConfirm('');
    setKillReason('');
  };

  const handleKill = async () => {
    if (!killTarget || !killReason.trim()) return;
    const restoring = Boolean(killTarget.billingSuspended);
    const confirmation = restoring ? 'UNSUSPEND' : 'SUSPEND';
    if (killConfirm !== confirmation) return;
    setKillLoading(true);
    try {
      if (killTarget.channelId) {
        if (restoring) await unsuspendChannel(killTarget.channelId, killReason.trim());
        else await suspendChannel(killTarget.channelId, killReason.trim());
      } else if (killTarget.sourceId && !restoring) {
        await killStream(killTarget.sourceId, killReason.trim());
      } else {
        throw new Error('Channel identifier is unavailable.');
      }
      showSnackbar(restoring ? 'Channel unsuspended and services restored.' : 'Channel suspended. Inputs, outputs and automation disabled.', 'success');
      setKillTarget(null);
      setKillConfirm('');
      setKillReason('');
      fetchStreams();
    } catch (e) {
      showSnackbar(restoring ? 'Failed to unsuspend channel' : 'Failed to suspend channel', 'error');
    } finally {
      setKillLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1760, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center">
              <LiveTvIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>Stream NOC</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>All channels, live ingest, readers, bandwidth, and output mix</Typography>
              </Box>
            </Stack>
            <SummaryMetric label="Inputs" value={stats.live} helper={`${stats.total} channels / ${stats.offline} offline`} color={theme.palette.primary.main} />
            <SummaryMetric label="Readers" value={stats.totalReaders} helper="active pulls" color={theme.palette.info.main} />
            <SummaryMetric label="Bandwidth" value={formatBitrate(stats.totalBandwidth)} helper={`in ${formatBytes(stats.totalBytesIn)}`} color={theme.palette.success.main} />
            <SummaryMetric label="Outputs" value={stats.activeOutputs} helper="active delivery" color={theme.palette.secondary.main} />
            <SummaryMetric label="Issues" value={stats.lowBitrate + stats.channelIssues} helper={`${stats.scheduledDeletion} scheduled / ${loadShedPercent}% load-shed`} color={stats.lowBitrate + stats.channelIssues ? theme.palette.error.main : theme.palette.info.main} />
          </Stack>
          <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={viewMode}
              onChange={(_, next: NocViewMode | null) => next && setViewMode(next)}
              aria-label="NOC view"
              sx={{ '& .MuiToggleButton-root': { width: 34, height: 34, p: 0, borderRadius: '6px' } }}
            >
              <ToggleButton value="list" aria-label="List view"><Tooltip title="List view"><ViewListIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
              <ToggleButton value="grid" aria-label="Grid view"><Tooltip title="Grid view"><GridViewIcon sx={{ fontSize: 18 }} /></Tooltip></ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="NOC Reports">
              <IconButton size="small" onClick={() => navigate('/admin/streams/reports')} sx={{ ...iconSx, width: 34, height: 34 }}>
                <AssessmentIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh NOC"><span><IconButton size="small" onClick={fetchStreams} disabled={loading} sx={{ ...iconSx, width: 34, height: 34 }}>{loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}</IconButton></span></Tooltip>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField fullWidth placeholder="Search channel, owner, source ID, path, or output..." variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>, sx: { borderRadius: 0 } }} />
          <TextField select label="Filter" value={filter} onChange={(e) => setFilter(e.target.value as ProtocolFilter)} size="small" sx={{ minWidth: 180 }} InputProps={{ sx: { borderRadius: 0 } }}>
            <MenuItem value="all">All Channels</MenuItem>
            <MenuItem value="live">Live</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="srtConn">SRT</MenuItem>
            <MenuItem value="rtmpConn">RTMP</MenuItem>
            <MenuItem value="viewers">With Readers</MenuItem>
            <MenuItem value="low_bitrate">Low Bitrate</MenuItem>
            <MenuItem value="issues">Issues Only</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {loading && streams.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10}><CircularProgress size={34} sx={{ mb: 2 }} /><Typography variant="body2" color="text.secondary">Loading active streams...</Typography></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ ...cardSx, p: 6, textAlign: 'center' }}><StreamIcon sx={{ fontSize: 52, color: 'text.disabled', mb: 1 }} /><Typography fontWeight={800} color="text.secondary">{search ? 'No matching channels' : 'No channels available'}</Typography><Typography sx={{ fontSize: '0.76rem', color: 'text.disabled' }}>{search ? 'Try another search term or filter.' : 'No channel inventory is available to this administrator.'}</Typography></Paper>
      ) : viewMode === 'grid' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
              xl: 'repeat(5, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {filtered.map((stream) => {
            const live = isStreamLive(stream);
            const severity = live ? bitrateSeverity(stream.bitrate) : 'default';
            return (
              <Paper
                key={`${stream.channelId || stream.sourceId || stream.pathName || stream.name}:grid`}
                elevation={0}
                sx={{ ...cardSx, borderRadius: 1, p: 1.25, minWidth: 0, borderTop: '3px solid', borderTopColor: live ? 'success.main' : 'action.disabled' }}
              >
                <Stack spacing={1.1} sx={{ height: '100%' }}>
                  <Stack direction="row" spacing={0.75} justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.84rem', fontWeight: 900 }}>{stream.channelName || stream.name}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.69rem', color: 'text.secondary' }}>{stream.userName || stream.userEmail || 'Unmatched owner'}</Typography>
                    </Box>
                    <Chip
                      label={live ? 'Live' : 'Offline'}
                      color={live ? 'success' : 'default'}
                      size="small"
                      sx={{ flexShrink: 0, borderRadius: 0.75, fontWeight: 900, height: 20, fontSize: '0.61rem' }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                    {live && <Chip label={protocolLabel(stream.sourceType)} color={sourceTypeKey(stream.sourceType) === 'srt' ? 'secondary' : 'primary'} size="small" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.63rem', fontWeight: 800 }} />}
                    <Chip label={stream.sourceLabel || sourceModeLabel(stream.sourceMode)} variant="outlined" size="small" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.63rem' }} />
                    {stream.billingSuspended && <Chip label="Suspended" color="error" size="small" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />}
                    {stream.scheduledDeletion && <Chip label="Deletion scheduled" color="warning" size="small" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />}
                  </Stack>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 0.7, columnGap: 1 }}>
                    <Box><Typography sx={labelSx}>Bitrate</Typography><Typography noWrap sx={{ ...monoSx, color: live ? `${severity}.main` : 'text.disabled' }}>{live ? formatBitrate(stream.bitrate) : 'No input'}</Typography></Box>
                    <Box><Typography sx={labelSx}>Uptime</Typography><Typography noWrap sx={monoSx}>{live ? formatUptime(stream.uptime) : '00:00'}</Typography></Box>
                    <Box><Typography sx={labelSx}>Readers</Typography><Typography noWrap sx={monoSx}>{Number(stream.viewers || 0).toLocaleString()}</Typography></Box>
                    <Box><Typography sx={labelSx}>Outputs</Typography><Typography noWrap sx={monoSx}>{stream.outputs?.active || 0}/{stream.outputs?.total || 0} active</Typography></Box>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={labelSx}>Codecs</Typography>
                    <Typography noWrap sx={{ ...monoSx, color: 'text.secondary' }}>{live ? formatCodecPair(stream.media?.videoCodec || stream.videoCodec, stream.media?.audioCodec || stream.audioCodec, 'video ? / audio ?') : 'No active media'}</Typography>
                  </Box>

                  <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ minHeight: 22 }}>
                    <Chip label={`Direct ${stream.outputs?.direct || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />
                    <Chip label={`Social ${stream.outputs?.social || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />
                    <Chip label={`HLS ${stream.outputs?.hls || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />
                    <Chip label={`Remap ${stream.outputs?.remapped || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0.75, height: 21, fontSize: '0.61rem' }} />
                  </Stack>

                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 'auto' }}>
                    <Tooltip title="Inspect channel"><span><IconButton size="small" disabled={!stream.channelId} onClick={() => stream.channelId && navigate(`/admin/streams/channels/${stream.channelId}`, { state: { stream } })} sx={{ ...iconSx, borderRadius: 0.75 }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                    <Tooltip title={stream.billingSuspended ? 'Unsuspend channel' : 'Suspend channel'}><span><IconButton size="small" color={stream.billingSuspended ? 'success' : 'error'} disabled={!stream.channelId && !stream.sourceId} onClick={() => confirmKill(stream)} sx={{ ...iconSx, borderRadius: 0.75 }}>{stream.billingSuspended ? <SettingsBackupRestoreIcon sx={{ fontSize: 16 }} /> : <PowerSettingsNewIcon sx={{ fontSize: 16 }} />}</IconButton></span></Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableRow>
                <TableCell sx={labelSx}>Channel / Owner</TableCell>
                <TableCell sx={labelSx}>Ingest</TableCell>
                <TableCell sx={labelSx}>Media / Network</TableCell>
                <TableCell sx={labelSx}>Outputs</TableCell>
                <TableCell sx={labelSx}>Readers</TableCell>
                <TableCell align="right" sx={labelSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((stream) => {
                const live = isStreamLive(stream);
                const severity = bitrateSeverity(stream.bitrate);
                return (
                  <TableRow key={`${stream.sourceId || stream.pathName || stream.name}`} hover>
                    <TableCell>
                      <Stack spacing={0.35}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>{stream.channelName || stream.name}</Typography>
                          <Chip label={live ? 'Live' : 'Offline'} color={live ? 'success' : 'default'} size="small" sx={{ borderRadius: 0.75, fontWeight: 900, height: 20, fontSize: '0.62rem' }} />
                          {stream.billingSuspended && <Chip label="Suspended" color="error" size="small" sx={{ borderRadius: 0.75, fontWeight: 800, height: 20, fontSize: '0.6rem' }} />}
                          {stream.scheduledDeletion && <Chip label="Deletion scheduled" color="warning" size="small" sx={{ borderRadius: 0.75, fontWeight: 800, height: 20, fontSize: '0.6rem' }} />}
                        </Stack>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{stream.userName || stream.userEmail || 'Unmatched owner'}</Typography>
                        <Typography sx={{ ...monoSx, color: 'text.disabled' }}>{stream.channelId || stream.pathName || stream.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.45}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip label={live ? protocolLabel(stream.sourceType) : 'Offline'} color={live ? (sourceTypeKey(stream.sourceType) === 'srt' ? 'secondary' : 'primary') : 'default'} size="small" sx={{ borderRadius: 0.75, fontWeight: 900, height: 22, fontSize: '0.66rem' }} />
                          <Chip label={stream.sourceLabel || sourceModeLabel(stream.sourceMode)} variant="outlined" size="small" sx={{ borderRadius: 0, height: 22, fontSize: '0.66rem' }} />
                        </Stack>
                        <Typography sx={{ ...monoSx, color: 'text.disabled' }}>{stream.sourceId || 'no source id'}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center"><AccessTimeIcon sx={{ fontSize: 15, color: 'text.secondary' }} /><Typography sx={monoSx}>{formatUptime(stream.uptime)}</Typography></Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.45}>
                        <Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 8, height: 8, bgcolor: live ? `${severity}.main` : 'action.disabled' }} /><Typography sx={monoSx}>{live ? formatBitrate(stream.bitrate) : 'No input'}</Typography></Stack>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>In {formatBytes(stream.bytesIn)} / Out {formatBytes(stream.bytesOut)}</Typography>
                        <Typography sx={{ ...monoSx, color: 'text.disabled' }}>{formatCodecPair(stream.media?.videoCodec || stream.videoCodec, stream.media?.audioCodec || stream.audioCodec, 'video ? / audio ?')}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                        <Chip label={`Direct ${stream.outputs?.direct || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 22, fontSize: '0.64rem' }} />
                        <Chip label={`Remap ${stream.outputs?.remapped || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 22, fontSize: '0.64rem' }} />
                        <Chip label={`Pull ${stream.outputs?.pull || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 22, fontSize: '0.64rem' }} />
                        <Chip label={`HLS ${stream.outputs?.hls || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 22, fontSize: '0.64rem' }} />
                        <Chip label={`HLS+ ${stream.outputs?.hlsPlus || 0}`} size="small" variant="outlined" sx={{ borderRadius: 0, height: 22, fontSize: '0.64rem' }} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={monoSx}>{Number(stream.viewers || 0).toLocaleString()}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{stream.readers?.[0]?.remoteAddr || 'no active reader detail'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Inspect channel"><span><IconButton size="small" disabled={!stream.channelId} onClick={() => stream.channelId && navigate(`/admin/streams/channels/${stream.channelId}`, { state: { stream } })} sx={iconSx}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>
                        <Tooltip title={stream.billingSuspended ? 'Unsuspend channel' : 'Suspend channel'}><span><IconButton size="small" color={stream.billingSuspended ? 'success' : 'error'} disabled={!stream.channelId && !stream.sourceId} onClick={() => confirmKill(stream)} sx={iconSx}>{stream.billingSuspended ? <SettingsBackupRestoreIcon sx={{ fontSize: 16 }} /> : <PowerSettingsNewIcon sx={{ fontSize: 16 }} />}</IconButton></span></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mt: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center"><CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} /><Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>Suspend Channel disables the channel, outputs, MediaBox, PlayKaster and the current input until admin re-enables service.</Typography></Stack>
      </Paper>

      <Dialog open={!!killTarget} onClose={() => setKillTarget(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle>{killTarget?.billingSuspended ? 'Unsuspend Channel' : 'Suspend Channel'}</DialogTitle>
        <DialogContent>
          <Alert severity={killTarget?.billingSuspended ? 'info' : 'warning'} sx={{ mb: 2, borderRadius: 0 }}>{killTarget?.billingSuspended ? 'This restores this channel and its purchased inputs, outputs, MediaBox and PlayKaster access.' : 'This suspends only this channel. Inputs, outputs, MediaBox and PlayKaster are disabled, while the user account and other channels remain available.'}</Alert>
          <Stack spacing={1.5}>
            <Paper elevation={0} sx={{ ...cardSx, p: 1.25 }}><Typography sx={labelSx}>Target</Typography><Typography sx={{ fontWeight: 900 }}>{killTarget?.channelName || killTarget?.name}</Typography><Typography sx={monoSx}>{killTarget?.sourceId}</Typography></Paper>
            <TextField label="Reason" fullWidth multiline minRows={3} value={killReason} onChange={(e) => setKillReason(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
            <TextField label={`Type ${killTarget?.billingSuspended ? 'UNSUSPEND' : 'SUSPEND'} to confirm`} fullWidth value={killConfirm} onChange={(e) => setKillConfirm(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setKillTarget(null)}>Cancel</Button><Button variant="contained" color={killTarget?.billingSuspended ? 'success' : 'error'} disabled={killLoading || killConfirm !== (killTarget?.billingSuspended ? 'UNSUSPEND' : 'SUSPEND') || !killReason.trim()} onClick={handleKill}>{killLoading ? 'Applying...' : killTarget?.billingSuspended ? 'Unsuspend Channel' : 'Suspend Channel'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};
